import {apiRequestWithAuth} from '@helpers/query/apiRequestWithAuth'
import {TypeOnErrorFn} from '@helpers/query/types'
import {BASE_TRACK_API_URL} from '@constants/config'
import {ChatConversation, ChatMessage, ChatRouteGroup} from '@containers/chat/types'

// Driver chat through the track-api proxy (/v1/chat) to mobileserver (/api/chat).
const CHAT_URL = `${BASE_TRACK_API_URL}/v1/chat`
const driverUrl = (driverId: number) => `${CHAT_URL}/drivers/${driverId}`

export const getDriverChatHistory = async (
    driverId: number,
    limit = 50,
    onError?: TypeOnErrorFn,
): Promise<ChatMessage[]> =>
    apiRequestWithAuth<ChatMessage[]>({
        url: `${driverUrl(driverId)}/history?limit=${limit}`,
        onError,
    })

export interface PollResult {
    messages?: ChatMessage[]
    statusUpdates?: Array<{id?: string; serverId?: string; status?: string}>
    serverTime?: number
}

// Incremental thread poll: only messages after since/lastIdHex plus status changes.
export const pollDriverChat = async (
    driverId: number,
    params: {since?: number; lastIdHex?: string; statusSince?: number},
    onError?: TypeOnErrorFn,
): Promise<PollResult> => {
    const search = new URLSearchParams()
    if (params.since != null) {
        search.set('since', String(params.since))
    }
    if (params.lastIdHex) {
        search.set('lastIdHex', params.lastIdHex)
    }
    if (params.statusSince != null) {
        search.set('statusSince', String(params.statusSince))
    }
    const query = search.toString()
    return apiRequestWithAuth<PollResult>({
        url: `${driverUrl(driverId)}/poll${query ? `?${query}` : ''}`,
        onError,
    })
}

export const sendDriverMessage = async (
    driverId: number,
    body: {message: string; messageType: string; clientMessageId: string},
    onError?: TypeOnErrorFn,
): Promise<unknown> =>
    apiRequestWithAuth<unknown>({
        url: `${driverUrl(driverId)}/messages`,
        options: {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {'Content-Type': 'application/json'},
        },
        onError,
    })

export const markDriverChatRead = async (driverId: number, onError?: TypeOnErrorFn): Promise<unknown> =>
    apiRequestWithAuth<unknown>({
        url: `${driverUrl(driverId)}/read`,
        options: {method: 'PUT'},
        onError,
    })

export const markDriverChatDelivered = async (driverId: number, onError?: TypeOnErrorFn): Promise<unknown> =>
    apiRequestWithAuth<unknown>({
        url: `${driverUrl(driverId)}/delivered`,
        options: {method: 'PUT'},
        onError,
    })

// Account-wide unread messages grouped by route (used by the global widget).
export const getUnreadGrouped = async (onError?: TypeOnErrorFn): Promise<ChatRouteGroup[]> =>
    apiRequestWithAuth<ChatRouteGroup[]>({
        url: `${CHAT_URL}/unread/grouped`,
        onError,
    })

// Per-user switch for the chat sound and popups, shared with legacy track (user configuration).
const CHAT_NOTIFICATIONS_URL = `${BASE_TRACK_API_URL}/v1/users/current/chat-notifications`

export const getChatNotifications = async (onError?: TypeOnErrorFn): Promise<{enabled: boolean}> =>
    apiRequestWithAuth<{enabled: boolean}>({
        url: CHAT_NOTIFICATIONS_URL,
        onError,
    })

export const updateChatNotifications = async (
    enabled: boolean,
    onError?: TypeOnErrorFn,
): Promise<{enabled: boolean}> =>
    apiRequestWithAuth<{enabled: boolean}>({
        url: CHAT_NOTIFICATIONS_URL,
        options: {
            method: 'PUT',
            body: JSON.stringify({enabled}),
            headers: {'Content-Type': 'application/json'},
        },
        onError,
    })

// All conversations of the account (the "all chats" tab).
export const getConversations = async (
    limit = 50,
    onError?: TypeOnErrorFn,
): Promise<{chats?: ChatConversation[]; total?: number}> =>
    apiRequestWithAuth<{chats?: ChatConversation[]; total?: number}>({
        url: `${CHAT_URL}/conversations?limit=${limit}`,
        onError,
    })